import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { Box, Button } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { ErrorLogsListSelector, LogsLoadingSelector, logThunks } from 'store/slices/logs.slice';
import { useEffect, useState } from 'react';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { ErrorLog } from 'interfaces/log.interfaces';
import ErrorLogDialog from 'components/Dialogs/LogDialogs/ErrorLogDialog';
import { formatDateTime } from 'helpers/formaters';
import LogsToolbar from './LogsToolbar';

const ErrorLogs = () => {
  const dispatch = useAppDispatch();
  const logsLoading = useSelector(LogsLoadingSelector);
  const logsList = useSelector(ErrorLogsListSelector);
  const [filteredLogs, setFilteredLogs] = useState<ErrorLog[]>(logsList);
  const [filteredValue, setFilteredValue] = useState<string>('');
  const [logDetailsOpen, setLogDetailsOpen] = useState<boolean>(false);
  const [selectedLogs, setSelectedLogs] = useState<ErrorLog>();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState<number>(10);

  const onViewClick = (logDetails: ErrorLog) => {
    setLogDetailsOpen(true);
    setSelectedLogs(logDetails);
  };
  const columnsDefinition: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Entry date/time',
      flex: 2,
      disableColumnMenu: true,
      valueGetter: (params: GridValueGetterParams) => formatDateTime(params.row.date),
    },
    {
      field: 'companyCode',
      headerName: 'Company',
      flex: 1,
      disableColumnMenu: true,
      valueGetter: (params: GridValueGetterParams) => params.row.companyCode || '-',
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 3,
      disableColumnMenu: true,
      cellClassName: 'super-app-theme--cell',
    },
    {
      field: 'id',
      headerName: 'Action',
      flex: 1,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Button
          variant='outlined'
          size='small'
          onClick={() => onViewClick(params.row)}
        >
          View
        </Button>
      ),
    },
  ];

  const clearLogs = () => {
    setConfirmationModalOpen(false);
    dispatch(logThunks.clearErrorLogs());
  };

  const retrieveLogs = () => {
    dispatch(logThunks.getErrorLogs());
  };

  const onFilter = (inputValue: string) => {
    setFilteredValue(inputValue);
    const filterValue = inputValue.toLowerCase();
    if (!inputValue) setFilteredLogs(logsList);
    else {
      setFilteredLogs(logsList.filter((log) => {
        if (formatDateTime(log.date.toString()).toLowerCase().includes(filterValue)
          || log.message.toString().toLowerCase().includes(filterValue)
          || (log?.companyCode && log.companyCode.toString().toLowerCase().includes(filterValue))) return log;

        return false;
      }));
    }
  };

  useEffect(() => {
    setFilteredLogs(logsList);
  }, [logsList]);

  useEffect(() => {
    retrieveLogs();
  }, []);

  return (
    <Box sx={{
      '& .super-app-theme--cell': {
        color: '#d32f2f',
        fontWeight: '400',
      },
    }}
    >
      <LogsToolbar
        onFilter={onFilter}
        filterValue={filteredValue}
        onClear={() => setConfirmationModalOpen(true)}
        onRefresh={retrieveLogs}
      />

      <Box>
        <DataGrid
          rows={filteredLogs}
          columns={columnsDefinition}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          autoHeight
          hideFooterSelectedRowCount
          loading={logsLoading}
          getRowId={(row: ErrorLog) => row.id}
          paginationMode='client'
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
      </Box>

      {logDetailsOpen && selectedLogs && (
        <ErrorLogDialog
          logDetails={selectedLogs}
          onCancelClick={() => setLogDetailsOpen(false)}
        />
      )}

      <ConfirmationModal
        open={confirmationModalOpen}
        message={`This action cannot be undone. 
                  Are you sure you want to delete all logs ?`}
        onConfirm={clearLogs}
        onCancel={() => setConfirmationModalOpen(false)}
      />
    </Box>

  );
};

export default ErrorLogs;
